/**
 * Système de contrôle d'accès pour les différents types de comptes
 *
 * Types de comptes :
 * - 'anonymous': Découverte limitée (Roulette + 3 premiers exercices)
 * - 'student': Accès complet avec email fictif
 * - 'email': Accès complet avec email réel
 *
 * Un élève (student/email) qui n'est rattaché à aucun prof (profile.teacherId vide)
 * est traité comme un visiteur anonyme : même périmètre limité (voir isRestrictedAccount).
 */

/**
 * Un compte est "restreint" (périmètre limité au chapitre 1) s'il est anonyme,
 * ou s'il s'agit d'un élève (student/email) sans prof rattaché.
 * Les profs ne sont jamais restreints. Tant que le profil n'est pas chargé : non restreint
 * (évite un flash de restriction pendant le chargement).
 * @param {object|null} userProfile - Document users/{uid} (avec .profile)
 * @param {object|null} firebaseUser - Utilisateur Firebase Auth (pour isAnonymous)
 * @returns {boolean}
 */
export function isRestrictedAccount(userProfile, firebaseUser = null) {
  if (firebaseUser?.isAnonymous === true) return true;
  const profile = userProfile?.profile;
  if (!profile) return false;
  if (profile.role === 'teacher' || profile.accountType === 'teacher') return false;
  if (profile.accountType === 'anonymous') return true;
  return !profile.teacherId;
}

/**
 * Mode gratuit : compte Firebase anonyme (« Continuer sans compte »).
 * Condition distincte de isRestrictedAccount, qui couvre aussi les élèves vérifiés sans professeur
 * (ceux-ci gardent l'accès complet au catalogue jusqu'à la bascule en liste blanche).
 */
export function isAnonymousUser(firebaseUser) {
  return firebaseUser?.isAnonymous === true;
}

export const FREE_MODE_MESSAGE = 'Crée un compte pour débloquer cette fonctionnalité';

/**
 * Raison de la restriction, pour adapter le message affiché.
 * - 'removed' : élève retiré par son prof (teacherId vide ET profile.removedAt présent)
 * @returns {'anonymous'|'no-teacher'|'removed'|null}
 */
export function getRestrictionReason(userProfile, firebaseUser = null) {
  if (!isRestrictedAccount(userProfile, firebaseUser)) return null;
  const isAnon = firebaseUser?.isAnonymous === true || userProfile?.profile?.accountType === 'anonymous';
  if (isAnon) return 'anonymous';
  return userProfile?.profile?.removedAt ? 'removed' : 'no-teacher';
}

/**
 * Routes accessibles à un compte restreint (le reste est bloqué).
 * - '/' et '/parcours' : accueil + chapitre 1 du Parcours (le passage au chapitre 2 est bloqué dans ParcoursContext)
 * - '/profile', '/mon-prof' : nécessaires pour saisir le code prof et débloquer le compte
 * - '/decouverte', '/roulette', '/exercise' : mêmes accès que l'anonyme historique (/exercise reste filtré
 *   par ProtectedExercise : 3 premiers exercices du nœud 1.1 hors Parcours)
 * - '/midi-check', '/settings', '/stats' : utilitaires sans contenu pédagogique
 */
const RESTRICTED_ALLOWED_PREFIXES = [
  '/parcours', '/profile', '/mon-prof', '/decouverte', '/roulette', '/exercise',
  '/midi-check', '/settings', '/stats',
];

/**
 * @param {string} pathname
 * @param {boolean} fromParcours - navigation lancée depuis une étape du Parcours (location.state.fromParcours)
 */
export function isPathAllowedForRestricted(pathname, fromParcours = false, anonymous = false) {
  if (pathname === '/') return true;
  if (fromParcours) return true; // étapes du chapitre 1 (chapitre 2+ inaccessible, cf. goToNextChapitre)
  // Mode gratuit (compte anonyme) : la Roulette en jeu libre est fermée (Roulette serveur : palier 1 seulement, via le Parcours)
  const prefixes = anonymous
    ? RESTRICTED_ALLOWED_PREFIXES.filter((prefix) => prefix !== '/roulette')
    : RESTRICTED_ALLOWED_PREFIXES;
  return prefixes.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + '/')
  );
}

/**
 * Liste des exercices autorisés pour les comptes anonymes
 * Basé sur le nodeId de l'exercice
 */
const ANONYMOUS_ALLOWED_NODES = [
  '1.1', // 3 premiers exercices du nœud 1.1 (gérés par ANONYMOUS_MAX_EXERCISES_PER_NODE)
];

const ANONYMOUS_MAX_EXERCISES_PER_NODE = {
  '1.1': 3, // Seulement les 3 premiers exercices du nœud 1.1
};

/**
 * Vérifie si un utilisateur a accès à un exercice
 * @param {string} accountType - Type de compte ('anonymous', 'student', 'email')
 * @param {string} nodeId - ID du nœud de l'exercice
 * @param {number} exerciseIndex - Index de l'exercice dans le nœud (0-based)
 * @returns {boolean} - true si l'utilisateur a accès
 */
export function hasAccessToExercise(accountType, nodeId, exerciseIndex = 0) {
  // Comptes student et email : accès complet
  if (accountType === 'student' || accountType === 'email') {
    return true;
  }

  // Compte anonyme : accès limité
  if (accountType === 'anonymous') {
    // Vérifier si le nœud est dans la liste autorisée
    if (!ANONYMOUS_ALLOWED_NODES.includes(nodeId)) {
      return false;
    }

    // Vérifier le nombre d'exercices autorisés pour ce nœud
    const maxExercises = ANONYMOUS_MAX_EXERCISES_PER_NODE[nodeId];
    if (maxExercises !== undefined && exerciseIndex >= maxExercises) {
      return false;
    }

    return true;
  }

  // Par défaut, refuser l'accès
  return false;
}

/**
 * Vérifie si un utilisateur a accès à la Roulette à Rythmes
 * @param {string} accountType - Type de compte
 * @returns {boolean}
 */
export function hasAccessToRoulette(accountType) {
  // Tous les types de comptes ont accès à la Roulette
  return true;
}

/**
 * Vérifie si un utilisateur a accès aux autres types d'exercices (Drill, Chrono, Metronome)
 * @param {string} accountType - Type de compte
 * @returns {boolean}
 */
export function hasAccessToChallenge(accountType) {
  // Seuls les comptes student et email ont accès aux Challenges
  return accountType === 'student' || accountType === 'email';
}

/**
 * Vérifie si la progression doit être sauvegardée
 * @param {string} accountType - Type de compte
 * @returns {boolean}
 */
export function shouldSaveProgress(accountType) {
  // Seuls les comptes student et email sauvegardent la progression
  return accountType === 'student' || accountType === 'email';
}

/**
 * Obtient le message à afficher quand l'accès est refusé
 * @param {string} accountType - Type de compte
 * @returns {object} - { title, message, ctaText }
 */
export const REMOVED_STUDENT_MESSAGE = 'Ton professeur t\'a retiré de sa liste. Saisis le code d\'un nouveau professeur pour reprendre ta progression.';

/** Bandeau « élève retiré » (accueil + profil) : même condition que le gate. */
export function shouldShowRemovedBanner(userProfile, firebaseUser = null) {
  return getRestrictionReason(userProfile, firebaseUser) === 'removed';
}

export function getAccessDeniedMessage(accountType, reason = null) {
  if (reason === 'anonymous') {
    return {
      title: '🔒 Contenu réservé',
      message: FREE_MODE_MESSAGE,
      ctaText: 'Créer mon compte',
      ctaLink: '/login'
    };
  }

  if (reason === 'removed') {
    return {
      title: '🔒 Code professeur requis',
      message: REMOVED_STUDENT_MESSAGE,
      ctaText: 'Entrer mon code professeur',
      ctaLink: '/profile',
      variant: 'no-teacher'
    };
  }

  if (reason === 'no-teacher') {
    return {
      title: '🔒 Code professeur requis',
      message: 'Ton compte n\'est pas encore relié à ton professeur. Entre le code que ton professeur t\'a donné pour débloquer toute l\'application.',
      ctaText: 'Entrer mon code professeur',
      ctaLink: '/profile',
      variant: 'no-teacher'
    };
  }

  if (accountType === 'anonymous') {
    return {
      title: '🔒 Contenu réservé',
      message: 'Cet exercice est réservé aux élèves inscrits. Crée ton compte élève pour accéder à tous les exercices et sauvegarder ta progression !',
      ctaText: 'Créer mon compte élève',
      ctaLink: '/login'
    };
  }

  return {
    title: '🔒 Accès refusé',
    message: 'Vous n\'avez pas accès à ce contenu.',
    ctaText: 'Retour à l\'accueil',
    ctaLink: '/'
  };
}

/**
 * Filtre une liste d'exercices selon le type de compte
 * @param {Array} exercises - Liste des exercices
 * @param {string} accountType - Type de compte
 * @param {string} nodeId - ID du nœud
 * @returns {Array} - Exercices filtrés
 */
export function filterExercisesByAccess(exercises, accountType, nodeId) {
  if (accountType === 'student' || accountType === 'email') {
    return exercises;
  }

  if (accountType === 'anonymous') {
    const maxExercises = ANONYMOUS_MAX_EXERCISES_PER_NODE[nodeId];
    if (maxExercises !== undefined) {
      return exercises.slice(0, maxExercises);
    }
  }

  return [];
}
